/**
 * After an in-app page change, keyboard and screen-reader users should land on the new page, not be
 * left on <body> because the link or row they activated has gone (WCAG 2.4.3; found live: opening a
 * Tenant, Reseller or User detail page from its list left focus on BODY).
 *
 * Only *lost* focus is rescued: if focus is still on something in the page (a sidebar link that
 * persists, a search box), it stays where the user put it. The rescue target is the first h1/h2 in
 * the innermost <main> (page titles are h2 under the portal's h1 brand; read out as the page name),
 * else that <main> itself. Lazy pages get a short grace period to render their heading.
 */
interface NavInfo { fromLocation?: unknown; pathChanged: boolean }
type Subscribe = (event: 'onRendered', fn: (e: NavInfo) => void) => () => void;

const ATTR = 'data-route-focus';

function focusLost(): boolean {
  const a = document.activeElement;
  return !a || a === document.body || !a.isConnected;
}

export function rescueFocus(attempt = 0): void {
  if (!focusLost()) { return; }
  const mains = document.querySelectorAll<HTMLElement>('main, [role=main]');
  const main = mains.length ? mains[mains.length - 1] : null; // innermost when nested
  const heading = main?.querySelector<HTMLElement>('h1, h2');
  if (!heading && attempt < 10) { setTimeout(() => rescueFocus(attempt + 1), 100); return; }
  const target = heading ?? main;
  if (!target) { return; }
  if (!target.hasAttribute('tabindex')) { target.setAttribute('tabindex', '-1'); target.setAttribute(ATTR, ''); }
  target.focus({ preventScroll: true });
}

export function installFocusOnNavigate(router: { subscribe: Subscribe }): () => void {
  return router.subscribe('onRendered', (e) => {
    if (!e.fromLocation || !e.pathChanged) { return; } // first load, or only ?search/#hash changed
    requestAnimationFrame(() => rescueFocus());
  });
}
